import "@babylonjs/core/Helpers/sceneHelpers";
import "@babylonjs/core/Rendering/boundingBoxRenderer";
import "@babylonjs/core/Materials/Textures/Loaders/envTextureLoader";
import "@babylonjs/core/Materials/Textures/Loaders/ddsTextureLoader";
import "@babylonjs/loaders/glTF";
import "@babylonjs/core/Misc/screenshotTools";

import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3, Vector2 } from '@babylonjs/core/Maths/math.vector';
import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { DefaultRenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline';
import { ImageProcessingConfiguration } from '@babylonjs/core/Materials/imageProcessingConfiguration';
import { AssetsManager } from '@babylonjs/core/Misc/assetsManager';

import { SCENE_URI, TEXTURES_URI, SIDES } from '../contants';
import { LoadingScreen } from './loader';
import { TextureSet } from './textures/textureSet';
import { MaterialCache, TextureSetCache, MeshType, Side, BootstrapOptions } from '../types';
import { SkyBox } from './environment/skybox';
import { TextureBulkUpdateManager } from './textures/textureBulkUpdateManager';
import { meshKey } from './utilities';
import { EnvironmentHelper } from '@babylonjs/core/Helpers/environmentHelper';
import { Tools } from '@babylonjs/core/Misc/tools';

export type MeshMaterialTextureSetPair = {
  alphaIndex: number
  clearCoatEnabled: boolean
  emissionEnabled: boolean
  material: string
  textureSet: string
}
export type MeshSidesIds = {
  left: MeshMaterialTextureSetPair[],
  right: MeshMaterialTextureSetPair[]
}

export type MeshTextureSetsSidesIds = {
  left: string,
  right: string
}

export type MeshMaterialTexture = {
  materials: MeshSidesIds,
  textureSets: MeshTextureSetsSidesIds
}

export type MeshIds = {
  [name: string]: MeshMaterialTexture
}

export type FocusMode = "left" | "center" | "right"

const lerp = (a: number, b: number, t: number): number => a + (t * (b - a))

const sleepAsync = async (timeout: number) => {
  return new Promise(resolve => {
    setTimeout(resolve, timeout);
  })
}

// This build a map of relationships between mesh type to materials and textures sets to use
// This is done so we can reuse texture sets with multiple materials since inner and outer
// materials share textures but not necessarily material settings.
const IDS: MeshIds = {
  "iem": {
    materials: {
      left: [
        {
          alphaIndex: 0,
          clearCoatEnabled: false,
          emissionEnabled: false,
          material: meshKey("iem", "inner", "left"),
          textureSet: meshKey("iem", "innerouter", "left")
        },
        {
          alphaIndex: 1,
          clearCoatEnabled: true,
          emissionEnabled: true,
          material: meshKey("iem", "outer", "left"),
          textureSet: meshKey("iem", "innerouter", "left")
        }
      ],
      right: [
        {
          alphaIndex: 0,
          clearCoatEnabled: false,
          emissionEnabled: false,
          material: meshKey("iem", "inner", "right"),
          textureSet: meshKey("iem", "innerouter", "right")
        },
        {
          alphaIndex: 1,
          clearCoatEnabled: true,
          emissionEnabled: true,
          material: meshKey("iem", "outer", "right"),
          textureSet: meshKey("iem", "innerouter", "right")
        }
      ]
    },
    textureSets: {
      left: meshKey("iem", "innerouter", "left"),
      right: meshKey("iem", "innerouter", "right")
    }
  },
  // "cable.test": {
  //   materials: {
  //     left: [{
  //       alphaIndex: 2,
  //       clearCoatEnabled: false,
  //       emissionEnabled: false,
  //       material: meshKey("cable", "test", "left"),
  //       textureSet: meshKey("cable", "test", "left")
  //     }],
  //     right: [{
  //       alphaIndex: 2,
  //       clearCoatEnabled: false,
  //       emissionEnabled: false,
  //       material: meshKey("cable", "test", "right"),
  //       textureSet: meshKey("cable", "test", "right")
  //     }],
  //   },
  //   textureSets: {
  //     left: meshKey("cable", "test", "left"),
  //     right: meshKey("cable", "test", "right"),
  //   }
  // },
  "inside": {
    materials: {
      left: [{
        alphaIndex: 1,
        clearCoatEnabled: false,
        emissionEnabled: false,
        material: meshKey("inside", "", "left"),
        textureSet: meshKey("inside", "", "left")
      }],
      right: [{
        alphaIndex: 1,
        clearCoatEnabled: false,
        emissionEnabled: false,
        material: meshKey("inside", "", "right"),
        textureSet: meshKey("inside", "", "right")
      }]
    },
    textureSets: {
      left: meshKey("inside", "", "left"),
      right: meshKey("inside", "", "right"),
    }
  }
}

export type ResizeCallback = () => { width?: number, height?: number }
export interface EngineOptions {
  canvas: HTMLCanvasElement
  focus?: FocusMode
  resizeCallback?: ResizeCallback
  vignetteEnabled?: Boolean;
  vignetteWeight?: Number;
}

export class JHADesignerEngine {
  public engine: Engine;
  public loadingScreen: LoadingScreen;
  public scene: Scene;
  public helper!: EnvironmentHelper;

  public camera!: ArcRotateCamera;
  public camTarget = new Vector3(0, 2, 0);
  public camPos = new Vector3(0, 2, -10);

  public materials: MaterialCache = {};

  public textureSets: TextureSetCache = {};

  public skybox?: SkyBox;

  public lightIntensities: { [name: string]: number } = {};
  public emissiveMinIntensity: number = .5;
  public emissiveMaxIntensity: number = 1;

  public emissiveMinGlowIntensity: number = .5;
  public emissiveMaxGlowIntensity: number = 1;

  public minGroundMirrorAmount: number = .2;
  public maxGroundMirrorAmount: number = .5;

  public renderPipeline!: DefaultRenderingPipeline;

  public assetsManager: AssetsManager;
  public textureBulkLoader = new TextureBulkUpdateManager(this);

  public sceneRootUrl: string = `${SCENE_URI}/`;
  public sceneFile: string = 'scene.gltf';

  private _screenshotWidth: number = 0;
  private _screenshotHeight: number = 0;

  private _lightIntensity: number = 1;

  private _lastCamTarget: Vector3 = Vector3.Zero();

  constructor(private canvas: HTMLCanvasElement, private resizeCallback?: ResizeCallback, private focus: FocusMode = "center") {
    this.resizeCanvas();

    this.engine = new Engine(this.canvas, true, {
      powerPreference: "high-performance",
      preserveDrawingBuffer: true,
    }, false);
    this.loadingScreen = new LoadingScreen("test loader");
    this.engine.loadingScreen = this.loadingScreen;
    this.scene = new Scene(this.engine);
    this.scene.preventDefaultOnPointerDown = false;
    this.assetsManager = new AssetsManager(this.scene);

    window.addEventListener("resize", () => {
      this.resizeCanvas();
      this.engine.resize();
    });
    window.addEventListener("orientationchange", () => {
      this.resizeCanvas();
      this.engine.resize();
    })
  }

  async enableControls(enable: boolean = true, cameraAlpha: number = Math.PI / 2, side?: Side) {
    this.camera.useAutoRotationBehavior = enable;

    if (!enable) {
      this.camera.inputs.attached.pointers.detachControl();
      this.camera.inputs.attached.keyboard.detachControl();
      this.camera.inputs.attached.mousewheel.detachControl();
      if (cameraAlpha != undefined) {
        this.camera.alpha = cameraAlpha;
      }

      this._lastCamTarget = this.camera.target;

      if (side) {
        console.log("Target side: ", side);
        //this.camera.target = side == "left"?new Vector3(1, 2, 0):new Vector3(-1, 2, 0);
      }
    } else {
      this.camera.inputs.attachInput(this.camera.inputs.attached.pointers);
      this.camera.inputs.attachInput(this.camera.inputs.attached.keyboard);
      this.camera.inputs.attachInput(this.camera.inputs.attached.mousewheel);
      this.camera.target = this._lastCamTarget;
    }
  }

  resizeCanvas() {
    if (this.resizeCallback) {
      const dimentions = this.resizeCallback();
      if (dimentions.width) {
        this.canvas.width = Math.floor(dimentions.width);
      }
      if (dimentions.height) {
        this.canvas.height = Math.floor(dimentions.height);
      }
    }
  }

  setSkyboxTexture(uri: string) {
    this.skybox?.update(uri);
  }

  getTextureSet(mesh: MeshType, side: Side) {
    return this.textureSets[IDS[mesh].textureSets[side]];
  }

  enableMeshParallax(mesh: MeshType, side: Side, enable: boolean) {
    const innerId = meshKey(mesh, "inner", side);
    const outerId = meshKey(mesh, "outer", side);
    const innerMaterial = this.materials[innerId];
    const outerMaterial = this.materials[outerId];
    innerMaterial.useParallax = enable;
    outerMaterial.useParallax = enable;
  }

  enableMeshParallaxOcclusion(mesh: MeshType, side: Side, enable: boolean) {
    const innerId = meshKey(mesh, "inner", side);
    const outerId = meshKey(mesh, "outer", side);
    const innerMaterial = this.materials[innerId];
    const outerMaterial = this.materials[outerId];
    innerMaterial.useParallaxOcclusion = enable;
    outerMaterial.useParallaxOcclusion = enable;
  }

  async queueScreenshot(width: number, height: number): Promise<string> {
    this._screenshotWidth = width;
    this._screenshotHeight = height;
    const lastCamAlpha = this.camera.alpha;
    const lastCamBeta = this.camera.beta;
    const lastTargetScreenOffset = this.camera.targetScreenOffset;
    const lastCamRadius = this.camera.radius;

    this.camera.useAutoRotationBehavior = false;
    this.camera.alpha = (Math.PI / 2) - ((Math.PI / 16) * 2);
    this.camera.beta = Math.PI / 2.5;
    this.camera.targetScreenOffset = Vector2.Zero();
    this.camera.radius = 2;
    this.camera.update();

    await sleepAsync(5);

    const data = await Tools.CreateScreenshotAsync(this.engine, this.camera, {
      width: this._screenshotWidth,
      height: this._screenshotHeight
    });

    this.camera.alpha = lastCamAlpha;
    this.camera.beta = lastCamBeta;
    this.camera.targetScreenOffset = lastTargetScreenOffset;
    this.camera.radius = lastCamRadius;
    this.camera.useAutoRotationBehavior = true;

    return data;
  }

  get lightIntensity(): number {
    return this._lightIntensity;
  }

  set lightIntensity(value: number) {
    this._lightIntensity = value;
    const meshId = IDS["iem"];
    for (let side of SIDES) {
      const materialIds = meshId.materials[side];

      for (let idPairs of materialIds) {
        const id = idPairs.material; // 1 mesh per material so they share ids.
        const material = this.materials[id]
        material.environmentIntensity = this._lightIntensity;
        material.emissiveIntensity = lerp(this.emissiveMaxIntensity, this.emissiveMinIntensity, this._lightIntensity);
      }
    }

    for (let light of this.scene.lights) {
      light.intensity = this.lightIntensities[light.name] * this._lightIntensity;
    }

    if (this.renderPipeline.glowLayer) {
      this.renderPipeline.glowLayer.intensity = lerp(this.emissiveMaxGlowIntensity, this.emissiveMinGlowIntensity, this._lightIntensity);
    }

    if (this.helper.groundMaterial) {
      this.helper.groundMaterial.reflectionAmount = lerp(this.minGroundMirrorAmount, this.maxGroundMirrorAmount, this._lightIntensity);
    }

  }

  setFocus(focus?: FocusMode) {
    if ( focus ) {
      this.focus = focus;
    }

    if (this.focus == "left") {
      this.camera.targetScreenOffset.set(-1.5, 0);
    } else if (this.focus == "right") {
      this.camera.targetScreenOffset.set(1.5, 0);
    } else {
      this.camera.targetScreenOffset.set(0, 0);
    }
  }

  async bootstrap(options?: BootstrapOptions) {
    this.skybox = new SkyBox(`${TEXTURES_URI}/env.dds`, this.scene, false);


    if ( options ) {
      if ( options.sceneRootUrl ) {
        this.sceneRootUrl = options.sceneRootUrl;
      }

      if ( options.sceneFile ) {
        this.sceneFile = options.sceneFile;
      }

    }

    if ( this.sceneRootUrl.charAt(this.sceneRootUrl.length - 1) != "/" ) {
      this.sceneRootUrl += "/";
    }
    
    try {
      await SceneLoader.AppendAsync(this.sceneRootUrl, this.sceneFile, this.scene)
    } catch (err) {
      console.error(err);
    }

    //const camTargetNode = this.scene.getTransformNodeByName('cam.target');
    // const camPosNode = this.scene.getTransformNodeByName('cam.pos');
    // !camTargetNode && console.warn("cam.target Missing");
    // !camPosNode && console.warn("cam.pos Missing");

    // if (camTargetNode) { this.camTarget = camTargetNode?.position; }
    // if (camPosNode) { this.camPos = camPosNode?.position; }

    const camera = new ArcRotateCamera('cam', Math.PI / 2, Math.PI / 2.5, 8, Vector3.Zero(), this.scene);
    this.camera = camera;
    camera.panningDistanceLimit = 3;
    camera.useAutoRotationBehavior = true;
    camera.wheelPrecision = 50;
    camera.lowerRadiusLimit = 1;
    camera.upperRadiusLimit = 10;
    camera.lowerRadiusLimit = 8;
    camera.upperRadiusLimit = 9;
    camera.radius = 3;
    this.setFocus();
    camera.minZ = 1;
    camera.setTarget(this.camTarget);
    camera.attachControl(this.canvas, false);

    if (camera.autoRotationBehavior) {
      camera.autoRotationBehavior.zoomStopsAnimation = true;
      camera.autoRotationBehavior.idleRotationSpinupTime = 2000;
      camera.autoRotationBehavior.idleRotationWaitTime = 60000 * 5;
    }

    const renderPipeline = this.renderPipeline = new DefaultRenderingPipeline("default", true, this.scene, [camera]);
    if (renderPipeline.isSupported) {
      renderPipeline.samples = 4;

      renderPipeline.fxaaEnabled = true;
      renderPipeline.fxaa.samples = 4;

      renderPipeline.glowLayerEnabled = true;
      if (renderPipeline.glowLayer) {
        renderPipeline.glowLayer.blurKernelSize = 4;
        renderPipeline.glowLayer.intensity = this.emissiveMinGlowIntensity;
        renderPipeline.glowLayer.neutralColor = new Color4(0, 0, 0, .3);
      }

      renderPipeline.imageProcessing.toneMappingEnabled = true;
      renderPipeline.imageProcessing.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
      renderPipeline.imageProcessing.contrast = 1;
      renderPipeline.imageProcessing.exposure = 1;

      renderPipeline.bloomEnabled = false;
      renderPipeline.bloomKernel = 25;
      renderPipeline.bloomWeight = .25;
      renderPipeline.bloomThreshold = .7;
      renderPipeline.bloomScale = .5;

      renderPipeline.imageProcessing.vignetteEnabled = true;
      renderPipeline.imageProcessing.vignetteBlendMode = ImageProcessingConfiguration.VIGNETTEMODE_MULTIPLY;
      renderPipeline.imageProcessing.vignetteColor = new Color4(0, 0, 0, 1);
      renderPipeline.imageProcessing.vignetteWeight = 5;
    }

    for (let meshName in IDS) {
      const meshId = IDS[meshName];
      for (let side of SIDES) {
        const materialIds = meshId.materials[side];
        const textureSetId = meshId.textureSets[side];
        const textureSize = 1024;

        // build texture sets
        if (!Reflect.has(this.textureSets, textureSetId)) {
          let textureSet = this.textureSets[textureSetId] = new TextureSet(textureSetId, textureSize, this.scene);
          // set default texture clear colors to mimic use
          textureSet.albedo.clearColor = new Color4(1, 1, 1, 0);
          // set default normals including mid parallax height bias
          textureSet.normal.clearColor = new Color4(.5, .5, 1, .5);
          // set 0 metallic and 1 roughness as default
          textureSet.metalRough.clearColor = new Color4(0, 1, 0, 1);
          // set black emission(off)
          textureSet.emissive.clearColor = new Color4(0, 0, 0, 1);
          textureSet.albedo.hasAlpha = true;
          textureSet.albedo.draw();
          textureSet.normal.draw();
          textureSet.metalRough.draw();
          textureSet.emissive.draw();
        }

        for (let idPairs of materialIds) {
          // cache materials per mesh
          const id = idPairs.material; // 1 mesh per material so they share ids.
          const material = this.materials[id] = new PBRMaterial(id, this.scene);
          const textureSet = this.textureSets[idPairs.textureSet];

          // assign textures to material
          material.useRoughnessFromMetallicTextureGreen = true;
          material.albedoColor = Color3.White();
          material.opacityTexture = material.albedoTexture = textureSet.albedo;
          material.bumpTexture = textureSet.normal;
          material.metallicTexture = textureSet.metalRough;
          material.useRoughnessFromMetallicTextureAlpha = false;
          material.useRoughnessFromMetallicTextureGreen = true;
          material.metallic = 1;
          material.roughness = 1;

          if (idPairs.emissionEnabled) {
            material.emissiveTexture = textureSet.emissive;
            material.emissiveColor = Color3.White();
            material.emissiveIntensity = this.emissiveMinIntensity;
          }

          // Clear coat defaults
          material.forceIrradianceInFragment = true;
          material.clearCoat.isEnabled = idPairs.clearCoatEnabled;
          material.clearCoat.intensity = 0.65;
          material.clearCoat.roughness = .05;
          material.environmentIntensity = 1;

          // Material tweaks
          //material.forceDepthWrite = true;
          material.brdf.useEnergyConservation = false;
          material.invertNormalMapY = false;
          material.ambientColor = Color3.White();
          material.useParallax = true;
          material.useParallaxOcclusion = true;
          material.parallaxScaleBias = 0;
          material.separateCullingPass = true;
          // required to avoid rendering backfaces on transparent materials
          material.needDepthPrePass = true;
          // no backfaces, for transparent materials we'll have a flipped mesh instead
          material.backFaceCulling = true;
          // enable alpha blending
          material.transparencyMode = 2; // MATERIAL_ALPHATESTANDBLEND
          material.useAlphaFromAlbedoTexture = true;
          material.alphaCutOff = .1;
          material.indexOfRefraction = 1.7;
          //material.refractionTexture = this.skybox.hdrTexture;

          // Assign PBR to mesh
          const mesh = this.scene.getMeshByName(id);
          if (mesh) {
            mesh.material = material;
            mesh.alphaIndex = idPairs.alphaIndex;
            mesh.renderingGroupId = idPairs.alphaIndex
          } else {
            console.warn("Missing Mesh: ", id);
          }
        }
      }

    }

    for (let light of this.scene.lights) {
      this.lightIntensities[light.name] = light.intensity;
    }


    this.scene.activeCamera = camera;

    // NOTE: Removed in favor of scene file lights and disabled shadows due to
    //       project time constrictions.
    // let light = new DirectionalLight("light.main", new Vector3(-.5, -1, 0), this.scene);
    // light.intensity = 10;
    // light.diffuse = Color3.White();
    // light.shadowEnabled = true;

    // this.enableLightShadows(["light.main"], (mesh, gen, map) => {
    //   if (mesh.name.startsWith('debug')) {
    //     return;
    //   }

    //   if (mesh.name != "floor") {
    //     gen.addShadowCaster(mesh);
    //   }
    //   mesh.receiveShadows = true;
    // });

    let helper = this.helper = this.scene.createDefaultEnvironment({
      skyboxSize: 100,
      skyboxColor: Color3.FromHexString("#14171a"),
      createSkybox: true,
      groundShadowLevel: 0.5,
      createGround: true,
      sizeAuto: true,
      rootPosition: Vector3.Zero(),
      groundMirrorAmount: this.maxGroundMirrorAmount,
      groundMirrorBlurKernel: 50,
      groundColor: Color3.FromHexString("#14171a"),
      enableGroundMirror: true
    }) as EnvironmentHelper;

    //helper?.setMainColor(Color3.Gray());
    if (renderPipeline.glowLayer) {
      if (helper) {
        renderPipeline.glowLayer.addExcludedMesh(helper.rootMesh);
      }

      for (let meshId of ["BackgroundPlane", "BackgroundHelper", "BackgroundSkybox"]) {
        const mesh = this.scene.getMeshByName(meshId) as Mesh;
        if (mesh) {
          renderPipeline.glowLayer.addExcludedMesh(mesh);
        }
      }
    }

    this.engine.runRenderLoop(() => {
      this.scene.render();
    });
  }

  // /**
  //  * Helper to create shadow generators per lights.
  //  * Pass an array of lights that should cast shadows and a callback function
  //  * to customize how these meshes interact with the shadows and light.
  //  */
  // enableLightShadows(lights: string[], customize: (mesh: AbstractMesh, gen: ShadowGenerator, map: RenderTargetTexture | null) => void) {
  //   for (let lightName of lights) {
  //     const light = this.scene.getLightByName(lightName) as IShadowLight;
  //     if (light) {
  //       const generator = new ShadowGenerator(512, light);
  //       generator.useContactHardeningShadow = true;
  //       generator.bias = 0.01;
  //       generator.normalBias = 0.05;
  //       generator.contactHardeningLightSizeUVRatio = 0.08;
  //       const shadowMap = generator.getShadowMap();

  //       for (let mesh of this.scene.meshes) {
  //         customize(mesh, generator, shadowMap);
  //       }
  //     } else {
  //       console.error("Light not found: ", lightName);
  //     }
  //   }

  // }
}
